/**
 * Apply decoded user-dashboard-theme payload (theme + typographySettings) to the DOM.
 * Matches partner portal save shape from UserDashboard.js.
 */

const STYLE_ID = "portal-dashboard-theme-font";
const CASCADE_STYLE_ID = "portal-dashboard-theme-cascade";

const CSS_VARS = [
  "--portal-sidenav-bg",
  "--portal-table-header-bg",
  "--portal-card-bg",
  "--portal-page-bg",
  "--portal-card-font",
  "--portal-button-bg",
  "--portal-button-font",
  "--portal-link-font",
  "--portal-font-family",
  "--portal-font-size",
  "--portal-font-weight",
  "--portal-font-style",
  "--portal-header-bg",
];

// Supports UTF-8 theme payloads (translated titles etc.) and legacy Latin1 base64
const base64ToUtf8 = (b64) => {
  const raw = atob(String(b64 ?? ""));
  try {
    return decodeURIComponent(escape(raw));
  } catch {
    return raw;
  }
};

export const decodeThemeBase64 = (themeBase64) => {
  if (!themeBase64) return null;
  try {
    return JSON.parse(base64ToUtf8(themeBase64));
  } catch (err) {
    console.error("Failed to decode portal theme:", err);
    return null;
  }
};

export const decodeTypographyBase64 = (typographyBase64) => {
  if (!typographyBase64) return null;
  try {
    return JSON.parse(base64ToUtf8(typographyBase64));
  } catch (err) {
    console.error("Failed to decode portal typography:", err);
    return null;
  }
};

/** Only live PUBLISHED themes apply on User Portal (DRAFT stays partner-only). */
export const isPublishedPortalTheme = (theme) => {
  if (!theme || typeof theme !== "object") return false;
  const status = theme.status;
  if (!status) return true; // legacy payloads without status
  return String(status).toUpperCase() === "PUBLISHED";
};

const clearPortalThemeFromDom = () => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  CSS_VARS.forEach((name) => root.style.removeProperty(name));

  const fontStyle = document.getElementById(STYLE_ID);
  if (fontStyle) fontStyle.remove();
  const cascadeStyle = document.getElementById(CASCADE_STYLE_ID);
  if (cascadeStyle) cascadeStyle.remove();

  if (document.body) {
    document.body.style.removeProperty("font-family");
    document.body.style.removeProperty("font-size");
    document.body.style.removeProperty("font-weight");
    document.body.style.removeProperty("font-style");
    document.body.classList.remove("portal-theme-applied");
  }
};

const buildFontDataUrl = (typography) => {
  if (!typography?.fontFile) return null;
  if (typography.fontFile.startsWith("data:")) return typography.fontFile;
  const mime = typography.fontMime || "application/octet-stream";
  return `data:${mime};base64,${typography.fontFile}`;
};

export const applyPortalThemeToDom = (portalTheme) => {
  if (typeof document === "undefined") return;

  if (!portalTheme || !isPublishedPortalTheme(portalTheme)) {
    clearPortalThemeFromDom();
    return;
  }

  const root = document.documentElement;
  const colors = portalTheme.colors || {};
  const darkColors = portalTheme.darkColors || {};
  const useDark = !!portalTheme._previewDark;
  const palette =
    useDark && Object.keys(darkColors).length ? darkColors : colors;

  const pageBg = palette.cardBackground || (useDark ? "#000000" : "#FFFFFF");

  const adjust = (hex, percent) => {
    if (!hex || !String(hex).startsWith("#")) return hex;
    const num = parseInt(String(hex).replace("#", ""), 16);
    if (Number.isNaN(num)) return hex;
    const r = Math.max(
      0,
      Math.min(255, (num >> 16) + Math.round((percent * 255) / 100)),
    );
    const g = Math.max(
      0,
      Math.min(255, ((num >> 8) & 0xff) + Math.round((percent * 255) / 100)),
    );
    const b = Math.max(
      0,
      Math.min(255, (num & 0xff) + Math.round((percent * 255) / 100)),
    );
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0").toUpperCase()}`;
  };

  // Card background owns the screen; sidenav/header/table are slight variations
  const sidenavBg =
    portalTheme.sidenav && !useDark
      ? portalTheme.sidenav
      : adjust(pageBg, useDark ? 14 : -6);
  const tableHeaderBg =
    portalTheme.tableHeader && !useDark
      ? portalTheme.tableHeader
      : adjust(pageBg, useDark ? 22 : -8);
  const headerBg = adjust(pageBg, useDark ? 10 : -4);

  if (sidenavBg) {
    root.style.setProperty("--portal-sidenav-bg", sidenavBg);
  }
  if (tableHeaderBg) {
    root.style.setProperty("--portal-table-header-bg", tableHeaderBg);
  }

  if (pageBg) {
    root.style.setProperty("--portal-card-bg", pageBg);
    root.style.setProperty("--portal-page-bg", pageBg);
    root.style.setProperty("--portal-header-bg", headerBg || pageBg);
  }
  if (palette.cardFont) {
    root.style.setProperty("--portal-card-font", palette.cardFont);
  }
  if (palette.buttonBackground) {
    root.style.setProperty("--portal-button-bg", palette.buttonBackground);
  }
  if (palette.buttonFont) {
    root.style.setProperty("--portal-button-font", palette.buttonFont);
  }
  if (palette.linkFont) {
    root.style.setProperty("--portal-link-font", palette.linkFont);
  }

  const typography = portalTheme.typography?.ENGLISH || {};
  const family =
    typography.fontFamily || (typography.fontFile ? "PortalCustomFont" : "");
  const fontSize =
    typography.fontSize != null
      ? `${typography.fontSize}`.replace(/px$/i, "") + "px"
      : "14px";
  const fontWeight = typography.fontWeight || "400";
  const fontStyle = typography.fontStyle || "normal";
  const fontSrc = buildFontDataUrl(typography);

  if (fontSrc && family) {
    let style = document.getElementById(STYLE_ID);
    if (!style) {
      style = document.createElement("style");
      style.id = STYLE_ID;
      document.head.appendChild(style);
    }
    const safeFamily = String(family).replace(/'/g, "\\'");
    style.textContent = `
      @font-face {
        font-family: '${safeFamily}';
        src: url('${fontSrc}');
        font-weight: ${fontWeight};
        font-style: ${fontStyle};
        font-display: swap;
      }
    `;
    root.style.setProperty(
      "--portal-font-family",
      `'${safeFamily}', system-ui, sans-serif`,
    );
  } else if (family) {
    const safeFamily = String(family).replace(/'/g, "\\'");
    root.style.setProperty(
      "--portal-font-family",
      `'${safeFamily}', system-ui, sans-serif`,
    );
  }

  root.style.setProperty("--portal-font-size", fontSize);
  root.style.setProperty("--portal-font-weight", fontWeight);
  root.style.setProperty("--portal-font-style", fontStyle);

  let cascade = document.getElementById(CASCADE_STYLE_ID);
  if (!cascade) {
    cascade = document.createElement("style");
    cascade.id = CASCADE_STYLE_ID;
    document.head.appendChild(cascade);
  }
  cascade.textContent = `
    body.portal-theme-applied {
      font-family: var(--portal-font-family, inherit);
      font-size: var(--portal-font-size, 14px);
      font-weight: var(--portal-font-weight, 400);
      font-style: var(--portal-font-style, normal);
    }
    body.portal-theme-applied *:not(svg):not(path):not(circle):not(rect):not(line):not(polyline):not(polygon) {
      font-family: inherit;
      font-style: inherit;
    }
    body.portal-theme-applied a,
    body.portal-theme-applied .portal-themed-link {
      color: var(--portal-link-font, inherit);
    }
    body.portal-theme-applied .sideBar-menu-click.active {
      background-color: var(--portal-button-bg, #fff) !important;
    }
    body.portal-theme-applied .sideBar-menu-click.active [class*="jds-text"],
    body.portal-theme-applied .sideBar-menu-click.active .jds-text {
      color: var(--portal-button-font, inherit) !important;
    }
    body.portal-theme-applied .status-card,
    body.portal-theme-applied .fiduciaryHeaderContainer {
      color: var(--portal-card-font, inherit);
    }
    body.portal-theme-applied button[class*="jds"],
    body.portal-theme-applied .jds-button-primary {
      background-color: var(--portal-button-bg, inherit);
      color: var(--portal-button-font, inherit);
    }
  `;

  if (document.body) {
    document.body.classList.add("portal-theme-applied");
    if (root.style.getPropertyValue("--portal-font-family")) {
      document.body.style.fontFamily = "var(--portal-font-family)";
    }
    document.body.style.fontSize = "var(--portal-font-size)";
    document.body.style.fontWeight = "var(--portal-font-weight)";
    document.body.style.fontStyle = "var(--portal-font-style)";
  }
};
